import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { IdentifierDomainOptions } from 'support/types';
import { Input } from 'components/Form/Input/Input/Input';
import { Radio } from 'components/Form/Radio/Radio';

export type PartnerIdentifierType = {
  partyIdentifierDomain?: IdentifierDomainOptions | undefined;
  partyIdentifier?: string | undefined;
};

const identifierDomainOptionsSchema = Yup.mixed<IdentifierDomainOptions>().oneOf(
  Object.values(IdentifierDomainOptions),
);

type PartnerIdentifierSchemaOptions = {
  connected: boolean;
  /** Whether the partner is already in the Procuros network (affects GLN input visibility) */
  partnerInNetwork?: boolean;
};

type PartnerIdentifierSchemaReturn = Yup.ObjectSchema<PartnerIdentifierType | Required<PartnerIdentifierType>>;

export const PartnerIdentifierSchema = (
  t: TFunction,
  options: PartnerIdentifierSchemaOptions,
): PartnerIdentifierSchemaReturn => {
  const { connected, partnerInNetwork = false } = options;

  if (connected) {
    return Yup.object({
      partyIdentifierDomain: identifierDomainOptionsSchema,
      partyIdentifier: Yup.string(),
    });
  }

  if (partnerInNetwork) {
    return Yup.object().shape({
      partyIdentifierDomain: identifierDomainOptionsSchema.required(),
      partyIdentifier: Yup.string().when('partyIdentifierDomain', {
        is: IdentifierDomainOptions.RECEIVER_INTERNAL,
        then: (schema) => schema.required(t('partners:request.partyIdentifier.required.error')),
        otherwise: (schema) => schema,
      }),
    });
  }

  return Yup.object().shape({
    partyIdentifierDomain: identifierDomainOptionsSchema.required(),
    partyIdentifier: Yup.string().required(t('partners:request.partyIdentifier.required.error')),
  });
};

type PartnerIdentifiersProps = {
  partnerInNetwork?: boolean;
};

export const PartnerIdentifiers = ({ partnerInNetwork = false }: PartnerIdentifiersProps) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<PartnerIdentifierType>();

  const selectedIdentifierDomain = useWatch({
    control,
    name: 'partyIdentifierDomain',
  });

  const needsIdentifierInput =
    !partnerInNetwork || selectedIdentifierDomain === IdentifierDomainOptions.RECEIVER_INTERNAL;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="text-base font-semibold leading-6 text-gray-900">{t('partners:request.identifier.title')}</div>
        <div className="text-sm leading-5 text-gray-700">{t('partners:request.identifier.description')}</div>
      </div>
      <Radio.Group className="!mt-0">
        <Radio.Item
          label={t('partners:request.identifier.GS1.label')}
          value={IdentifierDomainOptions.GS1}
          description={t('partners:request.identifier.GS1.description')}
          {...register('partyIdentifierDomain')}
        />
        <Radio.Item
          label={t('partners:request.identifier.RECEIVER_INTERNAL.label')}
          value={IdentifierDomainOptions.RECEIVER_INTERNAL}
          description={t('partners:request.identifier.RECEIVER_INTERNAL.description')}
          {...register('partyIdentifierDomain')}
        />
      </Radio.Group>

      {needsIdentifierInput && (
        <Input
          label={t(`partners:request.identifier.${selectedIdentifierDomain}.input.label`)}
          {...register('partyIdentifier')}
          errors={errors.partyIdentifier}
          description={
            selectedIdentifierDomain === IdentifierDomainOptions.RECEIVER_INTERNAL
              ? t('partners:request.identifier.RECEIVER_INTERNAL.input.description')
              : undefined
          }
          required
        />
      )}
    </div>
  );
};

/*
 * i18n keys:
 * t('partners:request.identifier.RECEIVER_INTERNAL.input.label')
 * t('partners:request.identifier.GS1.input.label')
 */
